import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { motion } from 'framer-motion';
import { Plus, Trash2, X, Clock, MapPin, Download, Briefcase } from 'lucide-react';
import ExportButton from './ExportButton';

const CareersPage = ({ showToast }) => {
    const [careerJobs, setCareerJobs] = useState([]);
    const [applications, setApplications] = useState([]);
    const [showAddJob, setShowAddJob] = useState(false);
    const [newJob, setNewJob] = useState({ role: '', type: 'Full Time', location: 'Remote', salary: '' });



    const fetchApplications = useCallback(async () => {
        try {
            const token = localStorage.getItem('admin_token');
            const response = await axios.get(`${API_URL}/career-applications`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setApplications(response.data);
        } catch (error) {
            console.error("Error fetching applications:", error);
            showToast('Failed to load applications', 'error');
        }
    }, [showToast]);

    useEffect(() => {
        fetchApplications();
        // Load local jobs for now as per current implementation
        setCareerJobs(JSON.parse(localStorage.getItem('career_jobs') || '[]'));
    }, [fetchApplications]);

    const handleDeleteJob = (id) => {
        if (window.confirm('Delete this job posting?')) {
            const updated = careerJobs.filter(j => j.id !== id);
            localStorage.setItem('career_jobs', JSON.stringify(updated));
            setCareerJobs(updated);
            showToast('Job posting deleted', 'success');
        }
    };

    const handleAddJob = (e) => {
        e.preventDefault();
        const jobToAdd = { ...newJob, id: Date.now() };
        const updated = [jobToAdd, ...careerJobs];
        localStorage.setItem('career_jobs', JSON.stringify(updated));
        setCareerJobs(updated);
        setNewJob({ role: '', type: 'Full Time', location: 'Remote', salary: '' });
        setShowAddJob(false);
        showToast('Job posting created', 'success');
    };

    const deleteApplication = async (id) => {
        if (window.confirm('Delete this application?')) {
            try {
                const token = localStorage.getItem('admin_token');
                await axios.delete(`${API_URL}/career-applications/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setApplications(applications.filter(app => app.id !== id));
                showToast('Application deleted', 'success');
            } catch (error) {
                console.error("Error deleting application:", error);
                showToast('Failed to delete application', 'error');
            }
        }
    };

    const getFullCVUrl = (path) => {
        const baseUrl = API_URL.replace('/api', '');
        return `${baseUrl}/storage/${path}`;
    };

    return (
        <div className="space-y-10">
            {/* Job Roles Management */}
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold text-dark-text">Active Job Roles</h2>
                    <button
                        onClick={() => setShowAddJob(true)}
                        className="flex items-center space-x-2 px-6 py-3 bg-brand-teal text-white rounded-xl font-bold hover:bg-brand-light transition-all"
                    >
                        <Plus className="w-5 h-5" />
                        <span>Post New Role</span>
                    </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {careerJobs.map(job => (
                        <div key={job.id} className="bg-white border border-slate-100 p-6 rounded-2xl relative group shadow-sm">
                            <button onClick={() => handleDeleteJob(job.id)} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                                <Trash2 className="w-4 h-4" />
                            </button>
                            <h3 className="text-lg font-bold text-dark-text mb-4">{job.role}</h3>
                            <div className="space-y-2 text-sm text-slate-500">
                                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-brand-teal" /> {job.type}</div>
                                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-teal" /> {job.location}</div>
                                <div className="text-brand-teal font-bold mt-2">{job.salary}</div>
                            </div>
                        </div>
                    ))}
                    {careerJobs.length === 0 && (
                        <div className="col-span-full p-8 text-center text-gray-500 border border-dashed border-slate-300 rounded-2xl">
                            No active job roles. Post a new role to get started.
                        </div>
                    )}
                </div>
            </div>

            {/* Applications Table */}
            <div className="bg-white border border-slate-100 shadow-sm rounded-[32px] overflow-hidden">
                <div className="p-8 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-dark-text">Job Applications</h2>
                    <ExportButton data={applications} filename="applications" label="Export" />
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left min-w-[1200px]">
                        <thead>
                            <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-widest font-bold">
                                <th className="px-6 py-4">ID</th>
                                <th className="px-6 py-4">Candidate</th>
                                <th className="px-6 py-4">Contact</th>
                                <th className="px-6 py-4">Job Details</th>
                                <th className="px-6 py-4">CV / Resume</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Applied At</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {applications.map((app) => (
                                <tr key={app.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-5 text-xs text-slate-400 font-mono">#{app.id}</td>
                                    <td className="px-6 py-5">
                                        <div className="font-bold text-dark-text">{app.first_name} {app.last_name}</div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <div className="text-sm text-slate-600">{app.email}</div>
                                        <div className="text-xs text-slate-400 mt-0.5">{app.phone}</div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <div className="font-semibold text-brand-teal text-sm">{app.job_role}</div>
                                        <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                                            <span>{app.job_type}</span>
                                            <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                                            <span>{app.job_location}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <a
                                            href={getFullCVUrl(app.cv_path)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 text-brand-teal hover:text-brand-light transition-colors group"
                                        >
                                            <div className="p-2 bg-brand-teal/10 rounded-lg group-hover:bg-brand-teal/20">
                                                <Download className="w-4 h-4" />
                                            </div>
                                            <span className="text-xs font-medium truncate max-w-[150px]">
                                                {app.cv_original_name}
                                            </span>
                                        </a>
                                    </td>
                                    <td className="px-6 py-5">
                                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase border ${app.status === 'New'
                                                ? 'bg-blue-50 text-blue-600 border-blue-200'
                                                : 'bg-slate-50 text-slate-600 border-slate-200'
                                            }`}>
                                            {app.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5">
                                        <div className="text-sm text-slate-500">
                                            {new Date(app.created_at).toLocaleDateString()}
                                        </div>
                                        <div className="text-[10px] text-slate-400 mt-0.5">
                                            {new Date(app.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-right">
                                        <button
                                            onClick={() => deleteApplication(app.id)}
                                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                            title="Delete Application"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {applications.length === 0 && (
                        <div className="p-12 text-center text-slate-400">
                            <div className="mb-4 flex justify-center">
                                <div className="p-4 bg-slate-50 rounded-full">
                                    <Briefcase className="w-8 h-8 text-slate-300" />
                                </div>
                            </div>
                            <p className="font-medium text-slate-500">No applications received yet</p>
                            <p className="text-xs mt-1">New applications will appear here once candidates apply.</p>
                        </div>
                    )}
                </div>
            </div>

            {showAddJob && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-6">
                    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-slate-100 p-8 rounded-[32px] w-full max-w-lg relative shadow-xl">
                        <button onClick={() => setShowAddJob(false)} className="absolute top-6 right-6 text-slate-400 hover:text-dark-text"><X className="w-6 h-6" /></button>
                        <h2 className="text-2xl font-bold text-dark-text mb-8">Post Job Role</h2>
                        <form onSubmit={handleAddJob} className="space-y-4">
                            <input required value={newJob.role} onChange={(e) => setNewJob({ ...newJob, role: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-dark-text placeholder-slate-400 focus:border-brand-teal focus:outline-none" placeholder="Job Role (e.g. Lead Developer)" />
                            <div className="grid grid-cols-2 gap-4">
                                <select value={newJob.type} onChange={(e) => setNewJob({ ...newJob, type: e.target.value })} className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-dark-text outline-none focus:border-brand-teal">
                                    <option className="bg-white" value="Full Time">Full Time</option>
                                    <option className="bg-white" value="Part Time">Part Time</option>
                                    <option className="bg-white" value="Contract">Contract</option>
                                </select>
                                <input required value={newJob.location} onChange={(e) => setNewJob({ ...newJob, location: e.target.value })} className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-dark-text placeholder-slate-400 outline-none focus:border-brand-teal" placeholder="Location" />
                            </div>
                            <button type="submit" className="w-full py-4 bg-brand-teal text-white font-bold rounded-xl mt-4 hover:bg-brand-light transition-all">Post Job</button>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default CareersPage;
